<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import Header from './components/Header.vue'
import Main from './components/Main.vue'
import Footer from './components/Footer.vue'
import Spinner from './components/Spinner.vue'
import Thankyou from './components/Thankyou.vue'
import { advertiserMapping } from './constants/selectOptions.js'

// Stores
import { useSessionStore } from '@/stores/sessionStore'
import { useFormStore } from '@/stores/formStore'
import { useLeadStore } from '@/stores/leadStore'

// Services
import { fetchLocation } from '@/services/locationService'
import { fetchArrayData } from '@/services/arrayService'
import { trackInitialImpressionOnce } from '@/services/trackingService'
import { sendEmailSignup, sendPhoneUpdate, sendAddressUpdate, sendArrayData, sendBuyerInfo } from '@/services/blueshiftDataService'
import { triggerFafSignup, triggerFullSubmit, triggerBuyerSequence } from '@/services/blueShiftService'
import { sendToCallCenter, shouldSendToCallCenter } from '@/services/callCenterService'
import { fireLpPixel, fireConversionPixels, fireFbCompleteRegistration, fireGtagAddress, initTrustedForm, initMetaPixel, initTikTokPixel } from '@/services/pixelService'
import { loadLpClickScript, initEverflowWithClickTracking } from '@/services/everflowService'
import { getAdvertiserFromClients, processLead } from '@/services/leadService'

// Config
import { getCampaignKeysForAff, DISCLAIMER_AIDS } from '@/config/campaignKeys'
import { getQuestionConfig, DEFAULT_QUESTION_ORDER } from '@/config/questions'

// Utils
import { sliderToDollarValue, sliderToNumberValue, calculateSliderGradient, parseArrayDebtResponse, toIsoDob } from '@/utils/formatters'
import { isYahooEmail } from '@/utils/validators'

// Initialize stores
const sessionStore = useSessionStore()
const formStore = useFormStore()
const leadStore = useLeadStore()

// Initialize session
sessionStore.initialize()

// URL parameters
const params = new URLSearchParams(window.location.search)

// Check for direct link affiliates that should redirect to FTC
const checkDirectLinkRedirect = () => {
  const affId = sessionStore.affiliateId
  if (affId === 'dlf' || affId === 'dlt') {
    window.location.href = 'https://consumer.ftc.gov/articles/how-get-out-debt'
    return true
  }
  return false
}

// Track initial impression
trackInitialImpressionOnce({
  sessionId: sessionStore.sessionId,
  variantId: sessionStore.variantId,
  sourceId: sessionStore.affiliateId
})

// Computed properties
const mainHeadline = computed(() => {
  if (window.location.hostname.includes('seniors')) {
    return 'Seniors Could Qualify for Debt Relief'
  }
  return 'Find Out If You Qualify For Economic Debt Relief in 2026'
})

const mainSubHeadline = computed(() => {
  if (window.location.hostname.includes('seniors')) {
    return "A lifetime of hard work shouldn't come with saddlebags of debt. Get back the retirement you were meant to have."
  }
  return 'Your search for debt relief begins here!'
})

const questionOrder = ref([...DEFAULT_QUESTION_ORDER])
const currentProgress = ref(0)
const transitionDirection = ref('forward')
const isRemovingQuestion = ref(false) // Boolean for transition animation
const showSpinner = ref(false)
const displayThankYou = ref(false)
const backButtonVisible = ref(false)
const trackingDisclosureVisible = ref(false)
const showArrayDisclaimer = ref(false)
const advertiser = ref(null)
const postAttemptNumber = ref(1)
const lpRequestId = ref(null)

// Array data storage
const arrayData = ref({
  collections: null,
  credit_score: null,
  delinquencies: null,
  total_unsecured_debt: null,
  tax_liens: null,
  open_student_loan_bal: null
})

// Reactive question state that components can modify
// This bridges between the static config and the mutable component state
const questionState = reactive({
  // Simple value questions
  amount: { value: formStore.values.amount },
  income: { value: formStore.values.income },
  employed: { value: formStore.values.employed },

  // Name question
  name: {
    first: formStore.name.first,
    last: formStore.name.last
  },

  // Email question
  email: {
    value: formStore.email.value,
    sendtoAC: formStore.email.sendtoAC
  },

  // Address question
  address: {
    address: formStore.address.address,
    zip: formStore.address.zip,
    city: formStore.address.city,
    state: formStore.address.state,
    overrideValidation: false
  },

  // DOB question (with maska support for input masking)
  dob: {
    day: formStore.dob.day,
    month: formStore.dob.month,
    year: formStore.dob.year,
    dayMaskaObject: { completed: false },
    monthMaskaObject: { completed: false },
    yearMaskaObject: { completed: false }
  },

  // Phone/Contact question (with maska support)
  phone: {
    value: formStore.phone.value,
    maskaObject: { unmasked: '', completed: false },
    tcpaLanguage: '',
    validationStatus: 'pending',
    overrideValidation: false
  }
})

// Get current question ID
const currentQuestionId = computed(() => questionOrder.value[currentProgress.value])

// Dynamic chat message for name question (must be defined before the watch that uses it)
const nameChatMessage = computed(() => {
  const debt = sliderToDollarValue(questionState.amount.value)
  const income = questionState.income.value

  if (income === '0') {
    return `<p>It looks like you have ${debt} in debt and no current income. I understand times can be tough, but don't worry – there are still options available to help you get back on your feet.</p>`
  }
  return `<p>Great! It looks like you have ${debt} in debt and your income is around $${Number(income).toLocaleString()} per month. Let's see what kind of savings we can find for you!</p>`
})

// Compute validated status for current question
function computeValidated(questionId, state) {
  switch (questionId) {
    case 'amount':
      return state.value !== null
    case 'income':
    case 'employed':
      return state.value !== null
    case 'name':
      return !!(state.first && state.last)
    case 'email':
      return !!state.value
    case 'phone':
      return state.overrideValidation ? false : !!(state.maskaObject?.completed)
    case 'address':
      return state.overrideValidation ? false : !!(state.address && state.zip)
    case 'dob':
      return !!(state.day && state.month && state.year)
    default:
      return state.value !== null && state.value !== undefined
  }
}

// Sync config and computed properties into questionState when question changes
// This runs as a side-effect outside of computed to avoid mutation-in-computed issues
watch(
  [currentQuestionId, nameChatMessage, () => sessionStore.empMode],
  ([questionId, chatMsg, empMode]) => {
    if (!questionId) return

    const baseQuestion = getQuestionConfig(questionId, {
      empMode,
      nameChatMessage: chatMsg
    })
    if (!baseQuestion) return

    const state = questionState[questionId]
    if (!state) return

    // Merge config properties into the state object
    Object.assign(state, baseQuestion)
    state.submit = () => handleQuestionSubmit(questionId)
  },
  { immediate: true }
)

// Get current question - returns the reactive questionState object
// Config is synced via the watch above, computed properties are added here
const currentQuestion = computed(() => {
  const questionId = currentQuestionId.value
  if (!questionId) return null

  const state = questionState[questionId]
  if (!state) return null

  // Add computed properties that need to update reactively
  state.validated = computeValidated(questionId, state)

  if (questionId === 'amount') {
    state.dollarValue = sliderToDollarValue(state.value)
    state.numberValue = sliderToNumberValue(state.value)
    state.backgroundGradientValue = calculateSliderGradient(state.value, state.min, state.max)
  }

  return state
})

// Progress percentage for header
const progressPercent = computed(() => {
  return Math.round((currentProgress.value / questionOrder.value.length) * 100)
})

// Show disclaimer for specific affiliates
const showDisclaimer = computed(() => {
  return DISCLAIMER_AIDS.includes(sessionStore.affiliateId)
})

// Navigation functions
function proceed() {
  // Track question step in GA
  if (typeof gtag === 'function') {
    gtag('event', currentQuestionId.value)
  }

  if (currentProgress.value < questionOrder.value.length - 1) {
    transitionDirection.value = 'forwards'
    isRemovingQuestion.value = true

    // Use requestAnimationFrame to ensure the removal triggers the leave animation
    // Then after a brief delay, show the next question
    requestAnimationFrame(() => {
      setTimeout(() => {
        currentProgress.value++
        isRemovingQuestion.value = false

        // Scroll to top
        document.getElementById('header')?.scrollIntoView()

        // Update URL hash
        history.pushState(
          { currentProgress: currentProgress.value },
          '',
          '#' + questionOrder.value[currentProgress.value]
        )
        localStorage.setItem('questionOrder', JSON.stringify(questionOrder.value))

        // Show back button after first question
        if (currentProgress.value > 0) {
          backButtonVisible.value = true
        }
      }, 50) // Small delay to allow the leave animation to start
    })
  }
}

function goBack() {
  if (currentProgress.value > 0) {
    transitionDirection.value = 'backwards'
    isRemovingQuestion.value = true

    setTimeout(() => {
      currentProgress.value--
      isRemovingQuestion.value = false

      // Scroll to top
      document.getElementById('header')?.scrollIntoView()

      // Update URL hash
      history.pushState(
        { currentProgress: currentProgress.value },
        '',
        '#' + questionOrder.value[currentProgress.value]
      )

      // Hide back button on first question
      if (currentProgress.value === 0) {
        backButtonVisible.value = false
      }
    }, 10)
  }
}

function hideBackButton() {
  backButtonVisible.value = false
}

function showBackButton() {
  backButtonVisible.value = true
}

function showTrackingDisclosure() {
  trackingDisclosureVisible.value = true
}

function hideTrackingDisclosure() {
  trackingDisclosureVisible.value = false
}

// Question submit handler - reads from questionState
async function handleQuestionSubmit(questionId) {
  const state = questionState[questionId]
  console.log('Question submit:', questionId, state)

  // Update form store based on question type
  switch (questionId) {
    case 'amount':
      formStore.setValue('amount', state.value)
      localStorage.setItem('amount', JSON.stringify(state.value))
      break

    case 'income':
      formStore.setValue('income', state.value)
      localStorage.setItem('income', JSON.stringify(state.value))
      break

    case 'employed':
      formStore.setValue('employed', state.value)
      localStorage.setItem('employed', JSON.stringify(state.value))
      break

    case 'name':
      formStore.setName(state.first, state.last)
      localStorage.setItem('name_first', JSON.stringify(state.first))
      localStorage.setItem('name_last', JSON.stringify(state.last))
      // Fetch location after name
      fetchLocationData()
      break

    case 'email':
      formStore.setEmail(state.value, state.sendtoAC)
      localStorage.setItem('email', JSON.stringify(state.value))
      localStorage.setItem('sendtoAC', JSON.stringify(state.sendtoAC))
      // Send to BlueShift
      if (state.sendtoAC && !isYahooEmail(state.value)) {
        await handleEmailSubmit(state.value)
      }
      break

    case 'address':
      formStore.setAddress(state.address, state.zip, state.city, state.state)
      localStorage.setItem('address', JSON.stringify(state.address))
      localStorage.setItem('zip', JSON.stringify(state.zip))
      localStorage.setItem('city', JSON.stringify(state.city))
      localStorage.setItem('state', JSON.stringify(state.state))
      // Send address update to BlueShift
      if (questionState.email.sendtoAC && !isYahooEmail(questionState.email.value)) {
        sendAddressUpdate({
          email: questionState.email.value,
          state: state.state,
          zip: state.zip
        })
      }
      // Fire address pixel
      fireGtagAddress()
      break

    case 'dob':
      formStore.setDob(state.month, state.day, state.year)
      localStorage.setItem('dob_month', JSON.stringify(state.month))
      localStorage.setItem('dob_day', JSON.stringify(state.day))
      localStorage.setItem('dob_year', JSON.stringify(state.year))
      // Fetch Array data after DOB (non-blocking - don't wait for response)
      fetchArrayDataAfterDob()
      break

    case 'phone':
      const phoneValue = state.maskaObject?.unmasked || state.value?.replace(/\D/g, '') || ''
      formStore.setPhone(phoneValue, state.tcpaLanguage, state.validationStatus)
      localStorage.setItem('phone', JSON.stringify(phoneValue))
      // Handle phone submission and lead posting
      await handlePhoneSubmit({
        phone: phoneValue,
        tcpaLanguage: state.tcpaLanguage,
        validationStatus: state.validationStatus
      })
      return // Don't proceed here, handlePhoneSubmit handles navigation

    default:
      if (state && state.value !== undefined) {
        formStore.setValue(questionId, state.value)
        localStorage.setItem(questionId, JSON.stringify(state.value))
      }
  }

  proceed()
}

// Fetch location data
async function fetchLocationData() {
  try {
    const location = await fetchLocation()
    if (location) {
      sessionStore.setIpAddress(location.ip)
      sessionStore.setLocationBackup(location.city, location.state, location.zip)
    }
  } catch (error) {
    console.error('Error fetching location:', error)
  }
}

// Fetch Array data after DOB
async function fetchArrayDataAfterDob() {
  // Wait for IP if not available yet
  if (!sessionStore.ipAddress) {
    await fetchLocationData()
  }

  try {
    const skipArray = params.get('sa') === '1'
    if (skipArray) {
      console.log('Skipping Array fetch (sa=1)')
      return
    }

    const dobIso = toIsoDob(questionState.dob.month, questionState.dob.day, questionState.dob.year)
    const data = await fetchArrayData({
      firstName: questionState.name.first,
      lastName: questionState.name.last,
      address: questionState.address.address,
      city: questionState.address.city,
      state: questionState.address.state,
      zip: questionState.address.zip,
      dob: dobIso,
      ipAddress: sessionStore.ipAddress
    })

    if (data) {
      arrayData.value = {
        collections: data.collections || null,
        credit_score: data.credit_score || null,
        delinquencies: data.delinquencies || null,
        total_unsecured_debt: data.total_unsecured_debt || null,
        tax_liens: data.tax_liens || null,
        open_student_loan_bal: data.open_student_loan_bal || null
      }

      // Send Array data to BlueShift
      if (questionState.email.sendtoAC && !isYahooEmail(questionState.email.value)) {
        sendArrayData({
          email: questionState.email.value,
          arrayDebt: data.total_unsecured_debt,
          creditScore: data.credit_score,
          collections: data.collections,
          delinquencies: data.delinquencies,
          taxLiens: data.tax_liens,
          studentLoans: data.open_student_loan_bal,
          birthYear: questionState.dob.year,
          birthMonth: questionState.dob.month,
          birthDay: questionState.dob.day
        })
      }
    }
  } catch (error) {
    console.error('Error fetching Array data:', error)
  }
}

// Handle email submit
async function handleEmailSubmit(email) {
  try {
    // Send to BlueShift
    await sendEmailSignup({
      email,
      ip: sessionStore.ipAddress,
      firstname: questionState.name.first,
      lastname: questionState.name.last,
      debt: sliderToNumberValue(questionState.amount.value),
      income: questionState.income.value
    })

    // Trigger BlueShift event
    await triggerFafSignup(email)

    // Fire FB CompleteRegistration
    fireFbCompleteRegistration()
  } catch (error) {
    console.error('Error handling email submit:', error)
  }
}

// Handle phone submit and lead posting
async function handlePhoneSubmit(phoneData) {
  // Capture compliance tokens BEFORE showing spinner (which removes the form from DOM)
  const jornayaToken = document.getElementById('leadid_token')?.value || ''
  const trustedFormToken = document.getElementsByName('xxTrustedFormToken')[0]?.value || ''

  console.log('Compliance tokens captured:', { jornayaToken, trustedFormToken })

  showSpinner.value = true

  try {
    // Send phone update to BlueShift
    if (questionState.email.sendtoAC && !isYahooEmail(questionState.email.value)) {
      await sendPhoneUpdate({
        email: questionState.email.value,
        phone: phoneData.phone.replace(/\D/g, ''),
        firstname: questionState.name.first,
        lastname: questionState.name.last,
        ip: sessionStore.ipAddress
      })

      // Trigger BlueShift fullsubmit event
      await triggerFullSubmit(questionState.email.value)
    }

    // Build lead data
    const campaignKeys = getCampaignKeys()
    const { verified_debt } = parseArrayDebtResponse(arrayData.value.total_unsecured_debt)

    const leadData = {
      // Required Leadspedia fields
      lp_campaign_id: campaignKeys.lp_campaign_id,
      lp_campaign_key: campaignKeys.lp_campaign_key,
      lp_response: 'json',
      pd_company_id: campaignKeys.pd_company_id || '',
      // Contact info
      address: questionState.address.address,
      zip_code: questionState.address.zip || sessionStore.backupZip,
      city: questionState.address.city || sessionStore.backupCity,
      state: questionState.address.state || sessionStore.backupState,
      email_address: questionState.email.value,
      first_name: questionState.name.first,
      last_name: questionState.name.last,
      phone_home: phoneData.phone.replace(/\D/g, ''),
      dob: `${questionState.dob.month}/${questionState.dob.day}/${questionState.dob.year}`,
      // Form answers
      income: questionState.income.value,
      employed: questionState.employed.value,
      debt_amount: sliderToNumberValue(questionState.amount.value),
      // Session/tracking data
      ip_address: sessionStore.ipAddress,
      user_agent: navigator.userAgent,
      landing_page: 'https://firstadvantage.io/app/',
      aff_id: sessionStore.affiliateId,
      lp_s1: sessionStore.formData.lp_s1 || '',
      lp_s2: sessionStore.formData.lp_s2 || '',
      lp_s3: sessionStore.formData.lp_s3 || '',
      lp_s4: sessionStore.formData.lp_s4 || '',
      lp_s5: sessionStore.formData.lp_s5 || '',
      // Verification tokens (captured before spinner shown)
      jornaya_lead_id: jornayaToken,
      trusted_form_id: trustedFormToken,
      // Array data
      array_collections: arrayData.value.collections,
      array_credit: arrayData.value.credit_score,
      array_delinquencies: arrayData.value.delinquencies,
      array_debt: arrayData.value.total_unsecured_debt,
      array_tax_liens: arrayData.value.tax_liens,
      array_student_loans: arrayData.value.open_student_loan_bal,
      verified_debt,
      // Variant/consent tracking
      split_test_variant: sessionStore.variantId,
      adr_fcra_disclosure: showArrayDisclaimer.value ? 'Yes' : 'No',
      tcpaLanguage: phoneData.tcpaLanguage || '',
      validationStatus: phoneData.validationStatus || 'verified'
    }

    // Post lead
    const { response } = await processLead({
      dataObj: leadData,
      attemptNumber: postAttemptNumber.value,
      formStore,
      onAttemptIncrement: () => ++postAttemptNumber.value
    })

    // Handle response
    const result = response?.data?.result
    const revenue = Number(response?.data?.price ?? 0)
    const clients = response?.data?.clients
    const redirectUrl = response?.data?.redirect_url

    // Get advertiser info
    let advertiserId = null
    if (clients) {
      advertiserId = getAdvertiserFromClients(clients)
      if (advertiserId && advertiserMapping[advertiserId]) {
        advertiser.value = advertiserMapping[advertiserId]
      }
    }

    // Send to call center if needed
    if (shouldSendToCallCenter(revenue, result)) {
      sendToCallCenter({
        firstName: questionState.name.first,
        lastName: questionState.name.last,
        email: questionState.email.value,
        phone: phoneData.phone.replace(/\D/g, ''),
        state: questionState.address.state || sessionStore.backupState,
        debt: sliderToNumberValue(questionState.amount.value),
        ip: sessionStore.ipAddress,
        aid: sessionStore.affiliateId
      })
    }

    // Fire conversion pixels
    if (revenue >= 45) {
      fireConversionPixels({
        affId: sessionStore.affiliateId,
        revenue,
        verifiedDebt: verified_debt
      })

      // Fire LP pixel if we have a request ID
      if (lpRequestId.value) {
        fireLpPixel(lpRequestId.value)
      }
    }

    // Send buyer info to BlueShift
    if (advertiser.value && questionState.email.sendtoAC && !isYahooEmail(questionState.email.value)) {
      sendBuyerInfo({
        email: questionState.email.value,
        buyer: advertiser.value.name,
        buyerPhone: advertiser.value.phone
      })

      // Trigger buyer sequence
      await triggerBuyerSequence(questionState.email.value)
    }

    // Store lead info
    leadStore.setSubmitted(true)
    leadStore.setLeadId(response?.data?.lead_id)
    leadStore.setRevenue(revenue)
    leadStore.setAdvertiser(advertiser.value)

    // Clear stored data
    clearStoredData()

    // Handle redirects
    handleRedirects({
      redirectUrl,
      advertiserId,
      revenue,
      verifiedDebt: verified_debt,
      leadData
    })

  } catch (error) {
    console.error('Error posting lead:', error)
    showSpinner.value = false
    displayThankYou.value = true
  }
}

// Handle post-submission redirects
function handleRedirects({ redirectUrl, advertiserId, revenue, verifiedDebt, leadData }) {
  // 1. If Leadspedia returns a redirect URL, always use it
  if (redirectUrl && redirectUrl.startsWith('https://')) {
    performRedirect(redirectUrl.replace(/\\u0026/g, '&'))
    return
  }

  // 2. Show thank you page for specific advertisers or if there's revenue
  const showThankYou =
    advertiserId === '2' ||
    (advertiserId === '24' && verifiedDebt > 1) ||
    advertiserId === '30' ||
    (advertiserMapping[advertiserId] && revenue > 1)

  if (showThankYou) {
    showSpinner.value = false
    displayThankYou.value = true
    return
  }

  // 3. Otherwise, trigger fallback redirect based on debt/revenue
  const turboLink = 'https://www.ab92trk.com/2X2ZMNN/45TJ3T/?sub1=fa1&sub2='

  let redirectUrlCustom = `https://firstadvantage.io/programs/save.html?vd=${verifiedDebt}`

  // Redirect to turbo link for specific conditions
  if (verifiedDebt === 9949 || (verifiedDebt === 0 && revenue < 1) || (verifiedDebt > 10000 && revenue < 1)) {
    const phone = questionState.phone.maskaObject?.unmasked || ''
    redirectUrlCustom = `${turboLink}${leadData.aff_id || ''}&first=${leadData.first_name || ''}&last=${leadData.last_name || ''}&phone=${phone}&email=${leadData.email_address || ''}&state=${leadData.state || ''}`
  }

  performRedirect(redirectUrlCustom)
}

// Perform redirect with small delay to allow tracking pixels to fire
function performRedirect(url) {
  setTimeout(() => {
    window.location.href = url
  }, 500)
}

// Get campaign keys based on affiliate and EMP mode
function getCampaignKeys() {
  const affId = sessionStore.affiliateId

  // Check for EMP split keys
  if (sessionStore.empSplitKeys) {
    return sessionStore.empSplitKeys
  }

  return getCampaignKeysForAff(affId)
}

// Clear stored form data
function clearStoredData() {
  const keysToRemove = [
    'amount', 'income', 'employed',
    'name_first', 'name_last', 'email', 'sendtoAC',
    'address', 'zip', 'city', 'state',
    'dob_month', 'dob_day', 'dob_year',
    'phone', 'questionOrder'
  ]
  keysToRemove.forEach(key => localStorage.removeItem(key))
}

// Handle browser back button
window.addEventListener('popstate', (event) => {
  if (event.state && typeof event.state.currentProgress === 'number') {
    transitionDirection.value = event.state.currentProgress < currentProgress.value ? 'backwards' : 'forwards'
    isRemovingQuestion.value = true

    setTimeout(() => {
      currentProgress.value = event.state.currentProgress
      isRemovingQuestion.value = false

      // Update back button visibility
      backButtonVisible.value = currentProgress.value > 0

      document.getElementById('header')?.scrollIntoView()
    }, 10)
  }
})

// Load stored form data on mount
function loadStoredFormData() {
  // Load simple values
  const storedAmount = localStorage.getItem('amount')
  if (storedAmount) {
    const val = JSON.parse(storedAmount)
    questionState.amount.value = val
    formStore.setValue('amount', val)
  }

  const storedIncome = localStorage.getItem('income')
  if (storedIncome) {
    const val = JSON.parse(storedIncome)
    questionState.income.value = val
    formStore.setValue('income', val)
  }

  const storedEmployed = localStorage.getItem('employed')
  if (storedEmployed) {
    const val = JSON.parse(storedEmployed)
    questionState.employed.value = val
    formStore.setValue('employed', val)
  }

  // Load name
  const storedFirst = localStorage.getItem('name_first')
  const storedLast = localStorage.getItem('name_last')
  if (storedFirst || storedLast) {
    const first = storedFirst ? JSON.parse(storedFirst) : ''
    const last = storedLast ? JSON.parse(storedLast) : ''
    questionState.name.first = first
    questionState.name.last = last
    formStore.setName(first, last)
  }

  // Load email
  const storedEmail = localStorage.getItem('email')
  const storedSendtoAC = localStorage.getItem('sendtoAC')
  if (storedEmail) {
    const email = JSON.parse(storedEmail)
    const sendtoAC = storedSendtoAC ? JSON.parse(storedSendtoAC) : true
    questionState.email.value = email
    questionState.email.sendtoAC = sendtoAC
    formStore.setEmail(email, sendtoAC)
  }

  // Load address
  const storedAddress = localStorage.getItem('address')
  const storedZip = localStorage.getItem('zip')
  const storedCity = localStorage.getItem('city')
  const storedState = localStorage.getItem('state')
  if (storedAddress || storedZip) {
    const address = storedAddress ? JSON.parse(storedAddress) : ''
    const zip = storedZip ? JSON.parse(storedZip) : ''
    const city = storedCity ? JSON.parse(storedCity) : ''
    const state = storedState ? JSON.parse(storedState) : ''
    questionState.address.address = address
    questionState.address.zip = zip
    questionState.address.city = city
    questionState.address.state = state
    formStore.setAddress(address, zip, city, state)
  }

  // Load DOB
  const storedMonth = localStorage.getItem('dob_month')
  const storedDay = localStorage.getItem('dob_day')
  const storedYear = localStorage.getItem('dob_year')
  if (storedMonth || storedDay || storedYear) {
    const month = storedMonth ? JSON.parse(storedMonth) : ''
    const day = storedDay ? JSON.parse(storedDay) : ''
    const year = storedYear ? JSON.parse(storedYear) : ''
    questionState.dob.month = month
    questionState.dob.day = day
    questionState.dob.year = year
    formStore.setDob(month, day, year)
  }

  // Load phone
  const storedPhone = localStorage.getItem('phone')
  if (storedPhone) {
    const phone = JSON.parse(storedPhone)
    questionState.phone.value = phone
    questionState.phone.maskaObject.unmasked = phone.replace(/\D/g, '')
    questionState.phone.maskaObject.completed = questionState.phone.maskaObject.unmasked.length === 10
    formStore.setPhone(phone)
  }
}

// Restore progress from URL hash
function restoreProgressFromHash() {
  if (!window.location.hash) return

  const storedQuestionOrder = localStorage.getItem('questionOrder')
  if (storedQuestionOrder) {
    questionOrder.value = JSON.parse(storedQuestionOrder)
  }

  const hashQuestion = window.location.hash.replace('#', '')
  let targetProgress = questionOrder.value.indexOf(hashQuestion)

  if (targetProgress === -1) return

  // Verify all previous questions are answered
  for (let i = 0; i < targetProgress; i++) {
    const qId = questionOrder.value[i]

    if (qId === 'name') {
      if (!formStore.name.first || !formStore.name.last) {
        targetProgress = i
        break
      }
    } else if (!formStore.isQuestionValid(qId)) {
      targetProgress = i
      break
    }

    history.pushState({ currentProgress: i }, '', '#' + qId)
  }

  currentProgress.value = targetProgress

  // Fetch data if we have enough info
  if (formStore.name.first) {
    fetchLocationData()
  }

  if (formStore.dob.day) {
    fetchArrayDataAfterDob()
  }
}

// Initialize on mount
onMounted(async () => {
  // Check for redirect affiliates
  if (checkDirectLinkRedirect()) return

  // Load stored form data
  loadStoredFormData()

  // Wait for gtag to be available, then fire init events
  const waitForGtag = setInterval(() => {
    if (typeof gtag === 'function') {
      clearInterval(waitForGtag)
      gtag('event', 'experience_impression', {
        exp_variant_string: sessionStore.variantName
      })
      gtag('event', 'form-init')
    }
  }, 500)

  // Initialize pixels
  initTrustedForm()

  // Initialize Meta pixel (with additional pixel for specific affiliates)
  const additionalPixelId = sessionStore.affiliateId === 'defb' ? '2491957387815568' : null
  initMetaPixel(additionalPixelId)

  // Initialize Everflow if applicable
  const campaignKeys = getCampaignKeysForAff(sessionStore.affiliateId)
  if (campaignKeys.everflow_aff_id) {
    initEverflowWithClickTracking({
      offerId: campaignKeys.everflow_offer_id || 320,
      affId: campaignKeys.everflow_aff_id,
      params,
      clickCampaignId: campaignKeys.click_campaign_id,
      onTransactionId: (transactionId) => {
        sessionStore.formData.lp_s5 = transactionId
      },
      onRequestId: (requestId) => {
        lpRequestId.value = requestId
      }
    })
  } else if (campaignKeys.click_campaign_id) {
    loadLpClickScript({
      clickCampaignId: campaignKeys.click_campaign_id,
      lpS5: sessionStore.formData.lp_s5,
      lpS2: sessionStore.formData.lp_s2,
      onRequestId: (requestId) => {
        lpRequestId.value = requestId
      }
    })
  }

  // Initialize TikTok for cvtt affiliate
  if (sessionStore.affiliateId === 'cvtt') {
    initTikTokPixel('CRU9FK3C77U3OBOND6SG')
  }

  // Restore progress from URL hash
  restoreProgressFromHash()

  // Set initial history state
  history.pushState(
    { currentProgress: currentProgress.value },
    '',
    '#' + questionOrder.value[currentProgress.value]
  )
})
</script>

<template>
  <Header
    :progressPercent="progressPercent"
    :showProgress="currentQuestion?.showProgress"
    :backButtonVisible="backButtonVisible"
    :advertiser="advertiser"
    :showDisclaimer="showDisclaimer"
    @showTrackingDisclosure="showTrackingDisclosure"
    @goBack="goBack" />
  <Main
    v-if="!showSpinner && !displayThankYou"
    :question="currentQuestion"
    :removeQuestion="isRemovingQuestion"
    :direction="transitionDirection"
    :backButtonVisible="backButtonVisible"
    :firstName="questionState.name.first"
    :mainHeadline="mainHeadline"
    :mainSubHeadline="mainSubHeadline"
    :showArrayDisclaimer="showArrayDisclaimer"
    :trackingDisclosureVisible="trackingDisclosureVisible"
    @showTrackingDisclosure="showTrackingDisclosure"
    @hideTrackingDisclosure="hideTrackingDisclosure"
    @proceed="currentQuestion?.submit"
    @goBack="goBack"
    @hideBackButton="hideBackButton"
    @showBackButton="showBackButton" />
  <section v-else-if="showSpinner && !displayThankYou" class="spinnerContainer">
    <Spinner :messages="['Verifying your information...','Checking if you qualify...','Calculating your savings...','Validating results...','Looking for more savings...','Almost there!']" />
  </section>
  <Thankyou
    v-else-if="displayThankYou"
    :advertiser="advertiser"
  />
  <Footer />
</template>

<style scoped>
  .spinnerContainer {
    min-height: 550px;
    min-height: 70vh;
    position: relative;
    padding-top: 50px;
  }
</style>
